import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ActivityIcon,
    UsersIcon,
    MailIcon,
    PlayCircleIcon,
    CheckCircle2Icon,
    XCircleIcon,
    RefreshCwIcon,
    SparklesIcon,
    MegaphoneIcon,
    StarIcon,
} from "lucide-react";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Card, CardContent } from "@component/card";
import { Pagination } from "@component/pagination";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { TrainingStatusPill } from "@component/training/status-pill";
import { CampaignStatusBadge } from "@component/training/campaign-status-badge";
import { RubricBar } from "@component/training/rubric-bar";
import { StatTile } from "@component/training/stat-tile";
import {
    getBiggestImprovers,
    getDashboardSummary,
    getElementInsights,
    getRecentActivity,
    getRecentCampaigns,
    getTodaysActions,
    getTopPerformers,
    listCampaigns,
    seedDemoCampaigns,
} from "@service/training";
import type {
    BiggestImprover,
    DashboardActivityItem,
    DashboardSummary,
    ElementInsights,
    RecentCampaign,
    TodaysActionsResult,
    TopPerformer,
} from "@service/training";
import type { TrainingCampaign } from "@type/training";
import { RUBRIC_LABELS, TRAINING_TYPE_LABEL } from "@type/training";
import { cn } from "@util/tailwind";

// Two-line activity rows: a colour-coded icon chip + a main line
// ("Ana · Q3 Sales") with a muted subtext detail underneath. Each log type
// gets its own accent colour so the feed is scannable at a glance — the chip
// colour is the visual cue for what kind of event happened.
const ACTIVITY_META: Record<
    DashboardActivityItem["type"],
    { icon: React.ComponentType<{ className?: string }>; cls: string }
> = {
    campaign_created: { icon: SparklesIcon, cls: "text-indigo-600 bg-indigo-50" },
    invite_sent: { icon: MailIcon, cls: "text-sky-600 bg-sky-50" },
    session_started: { icon: PlayCircleIcon, cls: "text-blue-600 bg-blue-50" },
    session_completed: { icon: CheckCircle2Icon, cls: "text-emerald-600 bg-emerald-50" },
    session_abandoned: { icon: XCircleIcon, cls: "text-amber-600 bg-amber-50" },
    retry_started: { icon: RefreshCwIcon, cls: "text-violet-600 bg-violet-50" },
};

function relativeTime(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
}

function initials(name: string | undefined | null) {
    if (!name) return "?";
    return name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

// The BE recent-activity feed emits "Invites sent to N employees" without
// singular handling, so fix "1 employees" → "1 employee" on render.
function fixEmployeePlural(detail?: string): string | undefined {
    return detail?.replace(/\b1 employees\b/g, "1 employee");
}

// Rows per page for the two list cards. Kept modest so neither card grows tall.
const PEOPLE_PAGE_SIZE = 5;
const ACTIVITY_PAGE_SIZE = 5;

// Time-window options for the Recent activity feed.
type ActivityWindow = "24h" | "7d" | "30d" | "all";
const DAY_MS = 24 * 60 * 60 * 1000;
const ACTIVITY_WINDOW_MS: Record<Exclude<ActivityWindow, "all">, number> = {
    "24h": DAY_MS,
    "7d": 7 * DAY_MS,
    "30d": 30 * DAY_MS,
};
const ACTIVITY_WINDOWS: { key: ActivityWindow; label: string }[] = [
    { key: "24h", label: "24h" },
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "all", label: "All" },
];

// Thin adapter over the shared <Pagination> so the dashboard cards use the
// same platform-wide pagination style (arrows + context-aware total). Converts
// the 0-based page state to the 1-based component API, and pins the footer to
// the bottom of the (flex-col) card via mt-auto. Renders nothing on a single
// page — the shared component bails when totalPages <= 1.
function Pager({
    page,
    pageSize,
    total,
    onChange,
    itemLabel = "item",
    alwaysShow = false,
}: {
    page: number;
    pageSize: number;
    total: number;
    onChange: (next: number) => void;
    itemLabel?: string;
    alwaysShow?: boolean;
}) {
    return (
        <Pagination
            currentPage={page + 1}
            totalPages={Math.max(1, Math.ceil(total / pageSize))}
            total={total}
            pageSize={pageSize}
            itemLabel={itemLabel}
            onPageChange={(p) => onChange(p - 1)}
            className="mt-auto pt-4"
            alwaysShow={alwaysShow}
        />
    );
}

// Sentinel for the Elements "All active campaigns" default in the campaign
// picker. Radix forbids empty-string Select values, and it doubles as the
// signal to request the cross-campaign aggregate (campaign_id omitted).
const ALL_CAMPAIGNS = "__all_campaigns__";

export default function TrainingDashboardPage() {
    const navigate = useNavigate();

    // Team insights row click → open the employee in context.
    // Preferred: deep-link to the campaign detail with the employee pre-filtered
    // (campaign detail reads ?search=<email>). Falls back to the People list with
    // the profile drawer (?openEmployee=) until the dashboard endpoints include
    // campaign_id (see BE handoff).
    const goToEmployee = (email: string, campaignId?: string | null) => {
        if (campaignId) {
            navigate(
                `/training/campaigns/${campaignId}?tab=employees&search=${encodeURIComponent(email)}`,
            );
            return;
        }
        navigate(
            `/training/performance?tab=employees&openEmployee=${encodeURIComponent(email)}`,
        );
    };
    // Recent campaigns section.
    const [recentCampaigns, setRecentCampaigns] = useState<RecentCampaign[] | null>(null);
    const [recentCampaignsLoading, setRecentCampaignsLoading] = useState(true);

    // Header KPI cards.
    const [summary, setSummary] = useState<DashboardSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    // Each list card has its own endpoint + loading state.
    const [todaysActions, setTodaysActions] = useState<TodaysActionsResult | null>(null);
    const [todaysActionsLoading, setTodaysActionsLoading] = useState(true);
    const [topPerformers, setTopPerformers] = useState<TopPerformer[] | null>(null);
    const [topPerformersLoading, setTopPerformersLoading] = useState(true);
    const [improvers, setImprovers] = useState<BiggestImprover[] | null>(null);
    const [improversLoading, setImproversLoading] = useState(true);
    const [activity, setActivity] = useState<DashboardActivityItem[] | null>(null);
    const [activityLoading, setActivityLoading] = useState(true);

    // Which of the people / aggregate views the combined "Team insights" card
    // shows. "elements" ranks the 5 rubric categories for one campaign.
    const [peopleTab, setPeopleTab] = useState<
        "attention" | "top" | "improved" | "elements"
    >("attention");

    // Elements view — per-element team aggregate (BE /training/insights/elements,
    // #3191). Defaults to the cross-campaign "All active campaigns (X)" aggregate
    // (ALL_CAMPAIGNS sentinel → campaign_id omitted); selecting a campaign swaps
    // to that one's ranking. The picker lists ACTIVE campaigns only (v1 scope).
    const [elementsCampaignId, setElementsCampaignId] = useState<string>(ALL_CAMPAIGNS);
    const [elementInsights, setElementInsights] = useState<ElementInsights | null>(null);
    const [elementInsightsLoading, setElementInsightsLoading] = useState(false);
    const [activeCampaigns, setActiveCampaigns] = useState<TrainingCampaign[] | null>(null);
    const [activeCampaignsLoading, setActiveCampaignsLoading] = useState(true);

    // Client-side pagination (data is fetched up to 50 per list, paged below).
    const [peoplePage, setPeoplePage] = useState(0);
    const [activityPage, setActivityPage] = useState(0);

    // Time window for the Recent activity feed (filtered client-side on `ts`).
    const [activityWindow, setActivityWindow] = useState<ActivityWindow>("7d");

    const [reloadTick] = useState(0);

    useEffect(() => {
        if (import.meta.env.VITE_USE_MOCK_TRAINING === "true") {
            seedDemoCampaigns();
        }
    }, [reloadTick]);

    // Recent campaigns list.
    useEffect(() => {
        let cancelled = false;
        setRecentCampaignsLoading(true);
        getRecentCampaigns(5)
            .then((c) => { if (!cancelled) setRecentCampaigns(c); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setRecentCampaignsLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Header KPI summary (active campaigns / people enrolled / completion / avg).
    useEffect(() => {
        let cancelled = false;
        setSummaryLoading(true);
        getDashboardSummary()
            .then((s) => { if (!cancelled) setSummary(s); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setSummaryLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Today's actions.
    useEffect(() => {
        let cancelled = false;
        setTodaysActionsLoading(true);
        getTodaysActions(50)
            .then((r) => { if (!cancelled) setTodaysActions(r); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setTodaysActionsLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Top performers.
    useEffect(() => {
        let cancelled = false;
        setTopPerformersLoading(true);
        getTopPerformers(50)
            .then((r) => { if (!cancelled) setTopPerformers(r); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setTopPerformersLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Biggest improvers.
    useEffect(() => {
        let cancelled = false;
        setImproversLoading(true);
        getBiggestImprovers(50)
            .then((r) => { if (!cancelled) setImprovers(r); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setImproversLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Recent activity.
    useEffect(() => {
        let cancelled = false;
        setActivityLoading(true);
        getRecentActivity(50)
            .then((r) => { if (!cancelled) setActivity(r); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setActivityLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Active-campaigns list for the Elements picker (active only — v1 scope).
    useEffect(() => {
        let cancelled = false;
        setActiveCampaignsLoading(true);
        listCampaigns()
            .then((all) => {
                if (!cancelled) setActiveCampaigns(all.filter((c) => c.status === "active"));
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setActiveCampaignsLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Fetch the per-element aggregate. Default (ALL_CAMPAIGNS) requests the
    // cross-campaign aggregate (campaign_id omitted); otherwise that campaign.
    useEffect(() => {
        if (peopleTab !== "elements") return;
        let cancelled = false;
        const campaignId = elementsCampaignId === ALL_CAMPAIGNS ? null : elementsCampaignId;
        setElementInsightsLoading(true);
        setElementInsights(null);
        getElementInsights(campaignId)
            .then((r) => { if (!cancelled) setElementInsights(r); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setElementInsightsLoading(false); });
        return () => { cancelled = true; };
    }, [peopleTab, elementsCampaignId]);

    // Old events shouldn't sit in a "recent" feed — scope to the chosen window.
    const visibleActivity =
        !activity || activityWindow === "all"
            ? activity ?? []
            : activity.filter(
                  (e) => Date.now() - new Date(e.ts).getTime() <= ACTIVITY_WINDOW_MS[activityWindow],
              );

    return (
        <TrainingAuthLayout>
            <div className="pb-12">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">
                        Training Dashboard
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Coach your team with AI roleplay.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                    <StatTile
                        label="Active campaigns"
                        value={summary?.active_campaigns ?? "—"}
                        loading={summaryLoading}
                        icon={MegaphoneIcon}
                        tooltip="Number of training campaigns that are currently active."
                    />
                    <StatTile
                        label="People enrolled"
                        value={summary?.people_enrolled ?? "—"}
                        loading={summaryLoading}
                        icon={UsersIcon}
                        tooltip="Total number of employees enrolled across all campaigns."
                    />
                    <StatTile
                        label="Completion rate"
                        value={summary ? `${Math.round(summary.completion_rate)}%` : "—"}
                        badge={summary ? `${summary.total_completed}/${summary.people_enrolled}` : undefined}
                        loading={summaryLoading}
                        icon={CheckCircle2Icon}
                        tooltip="Percentage of enrolled employees who have completed their training."
                    />
                    <StatTile
                        label="Avg score"
                        value={summary?.avg_score != null ? `${Math.round(summary.avg_score)}/100` : "—"}
                        loading={summaryLoading}
                        icon={StarIcon}
                        tooltip="Average score across all completed training sessions, out of 100."
                    />
                </div>

                {/* Row 1: Team insights (tabbed) + Recent activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* Team insights — one card, three people views. All three lists
                        are already fetched on mount; the toggle just swaps which is
                        shown. Rows are clickable through to the People tab. */}
                    <Card>
                        <CardContent className="p-5 flex flex-col min-h-[400px]">
                            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                                <div className="flex items-center gap-2">
                                    <UsersIcon className="w-4 h-4 text-blue-600" />
                                    <h2 className="text-base font-semibold text-gray-800">
                                        Team insights
                                    </h2>
                                </div>
                                <div className="inline-flex items-center gap-1">
                                    {([
                                        // `count` is a real backlog total for attention; for the
                                        // leaderboards it's just how many rows we're showing (capped
                                        // by the fetch limit), so it's styled neutrally, not as urgency.
                                        { key: "attention", label: "Needs attention", count: todaysActions?.total ?? 0, accent: "text-amber-600" },
                                        { key: "top", label: "Top performers", count: topPerformers?.length ?? 0, accent: "text-gray-400" },
                                        { key: "improved", label: "Most improved", count: improvers?.length ?? 0, accent: "text-gray-400" },
                                        // Elements ranks the 5 rubric categories; no
                                        // count badge (it's always 5, not a backlog).
                                        { key: "elements", label: "Elements", count: 0, accent: "text-gray-400" },
                                    ] as const).map((t) => {
                                        const active = peopleTab === t.key;
                                        return (
                                            <button
                                                key={t.key}
                                                onClick={() => { setPeopleTab(t.key); setPeoplePage(0); }}
                                                className={cn(
                                                    "px-3 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap",
                                                    active
                                                        ? "bg-blue-600 text-white"
                                                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                                                )}
                                            >
                                                {t.label}
                                                {t.count > 0 && (
                                                    <span className={cn("ml-1", active ? "text-blue-100" : t.accent)}>
                                                        {t.count}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Needs attention */}
                            {peopleTab === "attention" &&
                                (todaysActionsLoading ? (
                                    <RowsSkeleton />
                                ) : (todaysActions?.items.length ?? 0) === 0 ? (
                                    <div className="text-sm text-gray-500 py-6 text-center italic">
                                        Nothing needs your attention. Nice work.
                                    </div>
                                ) : (
                                    <>
                                    <ul className="list-plain space-y-1">
                                        {todaysActions!.items
                                            .slice(peoplePage * PEOPLE_PAGE_SIZE, (peoplePage + 1) * PEOPLE_PAGE_SIZE)
                                            .map((item) => (
                                            <li key={item.employee_email}>
                                                <button
                                                    onClick={() => goToEmployee(item.employee_email, item.campaign_id)}
                                                    className="w-full text-left flex items-center justify-between gap-3 rounded-md px-1.5 -mx-1.5 py-1.5 hover:bg-gray-50"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                                            {initials(item.employee_name)}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {item.employee_name}
                                                            </div>
                                                            <div className="text-xs text-amber-700 truncate">
                                                                {item.reason}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <TrainingStatusPill status={item.status} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <Pager
                                        page={peoplePage}
                                        pageSize={PEOPLE_PAGE_SIZE}
                                        total={todaysActions!.items.length}
                                        onChange={setPeoplePage}
                                        itemLabel="employee"
                                        alwaysShow
                                    />
                                    </>
                                ))}

                            {/* Top performers */}
                            {peopleTab === "top" &&
                                (topPerformersLoading ? (
                                    <RowsSkeleton />
                                ) : (topPerformers?.length ?? 0) === 0 ? (
                                    <div className="text-sm text-gray-500 py-6 text-center italic">
                                        No completed sessions yet.
                                    </div>
                                ) : (
                                    <>
                                    <ul className="list-plain space-y-1">
                                        {topPerformers!
                                            .map((p, i) => [p, i] as const)
                                            .slice(peoplePage * PEOPLE_PAGE_SIZE, (peoplePage + 1) * PEOPLE_PAGE_SIZE)
                                            .map(([p, i]) => (
                                            <li key={p.email + i}>
                                                <button
                                                    onClick={() => goToEmployee(p.email, p.campaign_id)}
                                                    className="w-full text-left flex items-center justify-between gap-3 rounded-md px-1.5 -mx-1.5 py-1.5 hover:bg-gray-50"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div
                                                            className={cn(
                                                                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                                                                i === 0
                                                                    ? "bg-amber-100 text-amber-700"
                                                                    : i === 1
                                                                    ? "bg-gray-100 text-gray-700"
                                                                    : "bg-gray-50 text-gray-600",
                                                            )}
                                                        >
                                                            {i + 1}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {p.name}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {p.persona_name}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">
                                                        {p.score}
                                                        <span className="text-gray-400">/100</span>
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <Pager
                                        page={peoplePage}
                                        pageSize={PEOPLE_PAGE_SIZE}
                                        total={topPerformers!.length}
                                        onChange={setPeoplePage}
                                        itemLabel="employee"
                                        alwaysShow
                                    />
                                    </>
                                ))}

                            {/* Most improved */}
                            {peopleTab === "improved" &&
                                (improversLoading ? (
                                    <RowsSkeleton />
                                ) : (improvers?.length ?? 0) === 0 ? (
                                    <div className="text-sm text-gray-500 py-6 text-center italic">
                                        No multi-attempt completions yet.
                                    </div>
                                ) : (
                                    <>
                                    <ul className="list-plain space-y-1">
                                        {improvers!
                                            .slice(peoplePage * PEOPLE_PAGE_SIZE, (peoplePage + 1) * PEOPLE_PAGE_SIZE)
                                            .map((p) => (
                                            <li key={p.email}>
                                                <button
                                                    onClick={() => goToEmployee(p.email, p.campaign_id)}
                                                    className="w-full text-left flex items-center justify-between gap-3 rounded-md px-1.5 -mx-1.5 py-1.5 hover:bg-gray-50"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                                            {initials(p.name)}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {p.name}
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {p.first_score}/100 → {p.latest_score}/100 · {p.attempts} attempts
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-sm font-bold text-emerald-700 tabular-nums flex-shrink-0">
                                                        +{p.delta}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <Pager
                                        page={peoplePage}
                                        pageSize={PEOPLE_PAGE_SIZE}
                                        total={improvers!.length}
                                        onChange={setPeoplePage}
                                        itemLabel="employee"
                                        alwaysShow
                                    />
                                    </>
                                ))}

                            {/* Elements — ranks the 5 rubric categories by the
                                team's mean 1–5 score. Defaults to the
                                cross-campaign "All active campaigns (X)"
                                aggregate; the picker swaps to a single active
                                campaign's ranking. */}
                            {peopleTab === "elements" && (
                                <ElementsView
                                    campaigns={activeCampaigns}
                                    campaignsLoading={activeCampaignsLoading}
                                    selectedCampaignId={elementsCampaignId}
                                    onSelectCampaign={setElementsCampaignId}
                                    insights={elementInsights}
                                    loading={elementInsightsLoading}
                                />
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent activity — single-line feed. Uses <ul> (not <ol>)
                        on purpose: index.css force-restores <ol> markers with
                        !important, which would show clipped "1./2." on the left. */}
                    <Card>
                        <CardContent className="p-5 flex flex-col min-h-[400px]">
                            <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
                                <div className="flex items-center gap-2">
                                    <ActivityIcon className="w-4 h-4 text-blue-600" />
                                    <h2 className="text-base font-semibold text-gray-800">
                                        Recent activity
                                    </h2>
                                </div>
                                <div className="inline-flex items-center gap-1">
                                    {ACTIVITY_WINDOWS.map((w) => {
                                        const active = activityWindow === w.key;
                                        return (
                                            <button
                                                key={w.key}
                                                onClick={() => { setActivityWindow(w.key); setActivityPage(0); }}
                                                className={cn(
                                                    "px-3 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap",
                                                    active
                                                        ? "bg-blue-600 text-white"
                                                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                                                )}
                                            >
                                                {w.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            {activityLoading ? (
                                <RowsSkeleton />
                            ) : visibleActivity.length === 0 ? (
                                <div className="text-sm text-gray-500 py-6 text-center italic">
                                    {(activity?.length ?? 0) === 0
                                        ? "No activity yet."
                                        : activityWindow === "all"
                                        ? "No activity yet."
                                        : "No activity in this period."}
                                </div>
                            ) : (
                                <>
                                <ul className="list-plain space-y-0.5 -mx-1.5">
                                    {visibleActivity
                                        .slice(activityPage * ACTIVITY_PAGE_SIZE, (activityPage + 1) * ACTIVITY_PAGE_SIZE)
                                        .map((e) => {
                                        const meta = ACTIVITY_META[e.type];
                                        const Icon = meta.icon;
                                        // Main line: "Employee · Campaign" (or just the
                                        // campaign when the event has no employee, e.g.
                                        // campaign_created). Subtext is the raw detail.
                                        const title = e.employee_name
                                            ? `${e.employee_name} · ${e.campaign_name}`
                                            : e.campaign_name;
                                        const subtext = fixEmployeePlural(e.detail);
                                        return (
                                            <li key={e.id}>
                                                <button
                                                    onClick={() => navigate(`/training/campaigns/${e.campaign_id}`)}
                                                    className="w-full text-left flex items-center justify-between gap-3 rounded-md px-1.5 py-1.5 hover:bg-gray-50"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div
                                                            className={cn(
                                                                "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center",
                                                                meta.cls,
                                                            )}
                                                        >
                                                            <Icon className="w-3.5 h-3.5" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {title}
                                                            </div>
                                                            {subtext && (
                                                                <div className="text-xs text-gray-500 truncate">
                                                                    {subtext}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 tabular-nums">
                                                        {relativeTime(e.ts)}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <Pager
                                    page={activityPage}
                                    pageSize={ACTIVITY_PAGE_SIZE}
                                    total={visibleActivity.length}
                                    onChange={setActivityPage}
                                    itemLabel="event"
                                />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Recent campaigns */}
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-gray-800">
                        Recent campaigns
                    </h2>
                    <button
                        onClick={() => navigate("/training/campaigns")}
                        className="text-sm text-blue-600 hover:underline"
                    >
                        View all →
                    </button>
                </div>

                {recentCampaignsLoading ? (
                    <Card>
                        <div className="divide-y divide-gray-100">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="p-4 flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="h-4 w-40 bg-gray-100 rounded animate-pulse mb-1.5" />
                                        <div className="h-3 w-56 bg-gray-100 rounded animate-pulse" />
                                    </div>
                                    <div className="h-4 w-12 bg-gray-100 rounded animate-pulse" />
                                </div>
                            ))}
                        </div>
                    </Card>
                ) : (recentCampaigns?.length ?? 0) === 0 ? (
                    <Card>
                        <CardContent className="p-12 text-center">
                            <div className="text-lg font-medium text-gray-900 mb-2">
                                No campaigns yet.
                            </div>
                            <div className="text-sm text-gray-600">
                                Create your first roleplay campaign to start coaching your team.
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <div className="divide-y divide-gray-100">
                            {recentCampaigns!.map((c) => {
                                const pct =
                                    c.employee_count > 0
                                        ? Math.round((c.completed_count / c.employee_count) * 100)
                                        : 0;
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => navigate(`/training/campaigns/${c.id}`)}
                                        className="w-full text-left p-4 hover:bg-gray-50 flex items-center gap-3"
                                    >
                                        <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                            <MegaphoneIcon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900 truncate">
                                                    {c.name}
                                                </span>
                                                {c.status && (
                                                    <CampaignStatusBadge status={c.status} />
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5 truncate">
                                                {TRAINING_TYPE_LABEL[c.training_type]} ·{" "}
                                                {c.persona_name}
                                                {c.team_leader ? ` · ${c.team_leader}` : ""}
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-sm font-medium text-gray-900">
                                                {c.avg_score !== null ? `${Math.round(c.avg_score)}/100` : "—"}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {pct}% completed ({c.completed_count}/{c.employee_count} employee{c.employee_count !== 1 ? "s" : ""})
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Card>
                )}
            </div>
        </TrainingAuthLayout>
    );
}


// Elements view of the Team insights card. Ranks the 5 rubric categories by the
// team's mean 1–5 score for the selected campaign. Reuses the training-reports
// leaderboard rank badge (#1 blue) and the shared <RubricBar> (label · compact
// bar · X.X/5) — no bespoke bars. The top/bottom rows are marked
// Highest/Lowest, and any sessions with no snapshotted rubric are surfaced.
function ElementsView({
    campaigns,
    campaignsLoading,
    selectedCampaignId,
    onSelectCampaign,
    insights,
    loading,
}: {
    campaigns: TrainingCampaign[] | null;
    campaignsLoading: boolean;
    selectedCampaignId: string;
    onSelectCampaign: (id: string) => void;
    insights: ElementInsights | null;
    loading: boolean;
}) {
    // Rank the 5 elements by mean (desc). Stable on ties by render order.
    const ranked = insights
        ? [...insights.elements].sort((a, b) => b.mean - a.mean)
        : [];
    const hasScores = ranked.some((e) => e.sessions > 0);
    const isAllCampaigns = selectedCampaignId === ALL_CAMPAIGNS;
    // X for "All active campaigns (X)" — active, scored campaigns from the BE
    // (campaigns_count). Falls back to the picker's active list before it lands.
    const allCount = insights?.campaigns_count ?? campaigns?.length ?? 0;

    return (
        <div className="flex flex-col flex-1">
            {/* Campaign picker — defaults to the cross-campaign aggregate. */}
            <div className="mb-3">
                <Select
                    value={selectedCampaignId}
                    onValueChange={onSelectCampaign}
                    disabled={campaignsLoading}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="All active campaigns" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL_CAMPAIGNS}>
                            All active campaigns{" "}
                            <span className="text-gray-400">({allCount})</span>
                        </SelectItem>
                        {(campaigns ?? []).map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {campaignsLoading ? (
                <RowsSkeleton rows={5} />
            ) : !isAllCampaigns && (campaigns?.length ?? 0) === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center italic">
                    No active campaigns yet.
                </div>
            ) : loading ? (
                <RowsSkeleton rows={5} />
            ) : !insights || !hasScores ? (
                <div className="text-sm text-gray-500 py-6 text-center italic">
                    No completed sessions with rubric scores yet.
                </div>
            ) : (
                <>
                    <ol className="space-y-1.5">
                        {ranked.map((e, i) => {
                            const isTop = i === 0;
                            const isBottom = i === ranked.length - 1;
                            return (
                                <li key={e.element} className="flex items-center gap-3">
                                    {/* Leaderboard rank badge (shared pattern). */}
                                    <div
                                        className={cn(
                                            "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                                            i === 0
                                                ? "bg-blue-600 text-white"
                                                : i === 1
                                                ? "bg-gray-100 text-gray-700"
                                                : "bg-gray-50 text-gray-600",
                                        )}
                                    >
                                        {i + 1}
                                    </div>
                                    {/* Shared rubric presentation: label · bar · X.X/5.
                                        Highest/Lowest marker sits to the RIGHT of the
                                        category label via the RubricBar `trailing` slot. */}
                                    <div className="flex-1 min-w-0">
                                        <RubricBar
                                            label={RUBRIC_LABELS[e.element] ?? e.element}
                                            value={e.mean}
                                            trailing={
                                                (isTop || isBottom) && (
                                                    <span
                                                        className={cn(
                                                            "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full flex-shrink-0",
                                                            isTop
                                                                ? "text-emerald-700 bg-emerald-50"
                                                                : "text-rose-700 bg-rose-50",
                                                        )}
                                                    >
                                                        {isTop ? "Highest" : "Lowest"}
                                                    </span>
                                                )
                                            }
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                    {insights.excluded_sessions > 0 && (
                        <div className="mt-auto pt-3 text-xs text-gray-400">
                            {insights.excluded_sessions} session
                            {insights.excluded_sessions === 1 ? "" : "s"} excluded (no rubric)
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// Per-card row skeleton (avatar + two text lines + trailing value).
function RowsSkeleton({ rows = 4 }: { rows?: number }) {
    return (
        <div className="space-y-2.5">
            {Array.from({ length: rows }).map((_, j) => (
                <div key={j} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="h-3 w-24 bg-gray-100 rounded animate-pulse mb-1.5" />
                        <div className="h-2 w-16 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="h-3 w-8 bg-gray-100 rounded animate-pulse flex-shrink-0" />
                </div>
            ))}
        </div>
    );
}
